import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { liveContextVmFingerprint } from '@/helpers/live-context-vm.helper';
import { mergeFlowPreservingLiveContext } from '@/helpers/merge-flow-preserving-live-context.helper';
import { create } from 'zustand';

interface FlowState {
  // PLAN mode document
  flowPlan?: FlowDto;
  // ORGANIZE mode document
  flowOrganize?: FlowDto;
  // Computed/current document (will be updated by flow detail component based on flowMode store)
  flow?: FlowDto;
  selectedSlot?: FlowSlotDto;
  setFlowPlan: (change?: FlowDto) => void;
  setFlowOrganize: (change?: FlowDto) => void;
  setFlow: (change?: FlowDto) => void;
  setSelectedSlot: (slot?: FlowSlotDto) => void;
}

export const useFlowStore = create<FlowState>()((set) => ({
  flowPlan: undefined,
  flowOrganize: undefined,
  flow: undefined,
  selectedSlot: undefined,
  setFlowPlan: (change) =>
    set((state) => ({
      flowPlan: change ? mergeFlowPreservingLiveContext(change, state.flowPlan ?? state.flow) : change,
    })),
  setFlowOrganize: (change) =>
    set((state) => ({
      flowOrganize: change ? mergeFlowPreservingLiveContext(change, state.flowOrganize ?? state.flow) : change,
    })),
  setFlow: (change) =>
    set((state) => {
      if (!change) {
        // Only update if flow is currently set
        if (state.flow) {
          return { flow: undefined };
        }
        return {}; // No change needed
      }

      const mergedChange = mergeFlowPreservingLiveContext(
        change,
        state.flow ?? (change.mode === 'PLAN' ? state.flowPlan : state.flowOrganize),
      );

      const slotsHaveChanged = (currentSlots?: FlowSlotDto[], nextSlots?: FlowSlotDto[]) => {
        if (!currentSlots && !nextSlots) return false;
        if (!currentSlots || !nextSlots) return true;
        if (currentSlots.length !== nextSlots.length) return true;
        const currentById = new Map(currentSlots.map((slot) => [slot.id, slot]));
        return nextSlots.some((nextSlot) => {
          const currentSlot = currentById.get(nextSlot.id);
          if (!currentSlot) return true;
          if (typeof currentSlot.content !== typeof nextSlot.content) return true;
          if (typeof nextSlot.content === 'string' || typeof currentSlot.content === 'string') {
            return currentSlot.content !== nextSlot.content;
          }
          if (!nextSlot.content || !currentSlot.content) return nextSlot.content !== currentSlot.content;
          if (
            'id' in nextSlot.content &&
            'id' in currentSlot.content &&
            nextSlot.content.id !== currentSlot.content.id
          ) {
            return true;
          }
          if (
            'version' in nextSlot.content &&
            'version' in currentSlot.content &&
            nextSlot.content.version !== currentSlot.content.version
          ) {
            return true;
          }
          if (
            'title' in nextSlot.content &&
            'title' in currentSlot.content &&
            nextSlot.content.title !== currentSlot.content.title
          ) {
            return true;
          }
          return false;
        });
      };

      const mediaHasChanged = (currentMedia?: FlowDto['media'], nextMedia?: FlowDto['media']) => {
        if (!currentMedia && !nextMedia) return false;
        if (!currentMedia || !nextMedia) return true;
        if (currentMedia.length !== nextMedia.length) return true;
        const currentById = new Map(currentMedia.map((media) => [media.id, media]));
        return nextMedia.some((nextMediaItem) => {
          const currentMediaItem = currentById.get(nextMediaItem.id);
          if (!currentMediaItem) return true;
          // Check if media item properties changed
          return (
            currentMediaItem.id !== nextMediaItem.id ||
            currentMediaItem.title !== nextMediaItem.title ||
            currentMediaItem.isRequested !== nextMediaItem.isRequested ||
            JSON.stringify(currentMediaItem.assets) !== JSON.stringify(nextMediaItem.assets)
          );
        });
      };

      const liveContextHasChanged = (
        currentLiveContext?: FlowDto['liveContext'],
        nextLiveContext?: FlowDto['liveContext'],
      ) => {
        if (!currentLiveContext && !nextLiveContext) return false;
        if (!currentLiveContext || !nextLiveContext) return true;
        if (currentLiveContext.isRunning !== nextLiveContext.isRunning) return true;
        if (currentLiveContext.showMode !== nextLiveContext.showMode) return true;
        const currentVmKey = currentLiveContext.vms?.map(liveContextVmFingerprint).join('|') ?? '';
        const nextVmKey = nextLiveContext.vms?.map(liveContextVmFingerprint).join('|') ?? '';
        return currentVmKey !== nextVmKey;
      };

      // Check if the flow has actually changed (idempotent check)
      // Compare by id, version, slot.id, slot.version, durationPlannedMinutes, slot content changes, and media changes
      const currentFlow = state.flow;
      const slotOrderKey = (slots?: FlowSlotDto[]) => (slots ? slots.map((slot) => slot.id).join('|') : '');
      const hasChanged =
        !currentFlow ||
        currentFlow.id !== mergedChange.id ||
        currentFlow.version !== mergedChange.version ||
        JSON.stringify(currentFlow.publishedModes) !== JSON.stringify(mergedChange.publishedModes) ||
        currentFlow.slot?.id !== mergedChange.slot?.id ||
        currentFlow.slot?.version !== mergedChange.slot?.version ||
        currentFlow.slot?.startOffsetMinutes !== mergedChange.slot?.startOffsetMinutes ||
        currentFlow.slot?.durationPlannedMinutes !== mergedChange.slot?.durationPlannedMinutes ||
        JSON.stringify(currentFlow.slot?.day) !== JSON.stringify(mergedChange.slot?.day) ||
        JSON.stringify(currentFlow.slot?.publishedModes) !== JSON.stringify(mergedChange.slot?.publishedModes) ||
        currentFlow.durationPlannedMinutes !== mergedChange.durationPlannedMinutes ||
        slotOrderKey(currentFlow.slots) !== slotOrderKey(mergedChange.slots) ||
        slotsHaveChanged(currentFlow.slots, mergedChange.slots) ||
        mediaHasChanged(currentFlow.media, mergedChange.media) ||
        liveContextHasChanged(currentFlow.liveContext, mergedChange.liveContext) ||
        JSON.stringify(currentFlow.mediaSettings ?? null) !== JSON.stringify(mergedChange.mediaSettings ?? null) ||
        JSON.stringify(currentFlow.effectiveMediaSettings ?? null) !==
          JSON.stringify(mergedChange.effectiveMediaSettings ?? null) ||
        JSON.stringify(currentFlow.mediaSettingsCapabilities ?? null) !==
          JSON.stringify(mergedChange.mediaSettingsCapabilities ?? null) ||
        JSON.stringify(currentFlow.mediaPlaylists ?? null) !== JSON.stringify(mergedChange.mediaPlaylists ?? null);

      // If nothing changed, don't update state (prevents ping-pong loops)
      if (!hasChanged) {
        return {}; // No change needed
      }

      // Store based on mode field
      if (mergedChange.mode === 'PLAN') {
        return {
          flowPlan: mergedChange,
          flow: mergedChange, // Will be updated by flow detail component based on flowMode
        };
      } else {
        return {
          flowOrganize: mergedChange,
          flow: mergedChange, // Will be updated by flow detail component based on flowMode
        };
      }
    }),
  setSelectedSlot: (slot) => set(() => ({ selectedSlot: slot })),
}));
